// imports
import React, { useRef, useState } from 'react'
import { Box } from '@bosch/react-frok'

// components
import MessageWrapper from '@/ui/components/MessageWrapper/MessageWrapper'
import SolutionCardWrapper from '@/ui/components/GroupMessage/Cards/SolutionCard'
import ChatImage from '@/ui/components/ChatImage/ChatImage'
import HorizontalPager from '@/ui/components/HorizontalPager/HorizontalPager'

// hooks & utils
import { getAssistantIllustration } from '@/hooks/useAssistantIllustration'
import useHorizontalPaging from '@/hooks/useHorizontalPaging'
import useWindowSize from '@/hooks/useWindowSize'
import type { InternalMessage } from '@/utils/ai-adapters/xdc/types'
import type { ChatFeatureValue } from '@/ui/components/ChatFeatures/types/ChatFeaturesTypes'
import type { OutputFeatures } from '@/shared/types/messageTools'
// styles
import './GroupMessage.scss'

export type GroupMessageType = 'solution'

type GroupMessage = InternalMessage & {
    message?: string
}

export interface GroupMessageProps {
    type: GroupMessageType
    messages: GroupMessage[]
    features?: Record<string, ChatFeatureValue>
    outputFeatures?: OutputFeatures
    isDarkTheme?: boolean
    isLastMessage?: boolean
    isStreaming?: boolean
}

const GroupMessageComponent = ({
    messages,
    features,
    outputFeatures,
    isDarkTheme,
    isStreaming,
}: GroupMessageProps) => {
    const { isMobile } = useWindowSize()
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const { page, pageCount, goToPage } = useHorizontalPaging(scrollRef, messages.length)
    // Mobile shows the next card peeking in, desktop gets the buttons and the page indicator
    const showPager = !isMobile && !isStreaming && pageCount > 1

    return (
        <div className="message-wrapper">
            <ChatImage
                image={getAssistantIllustration()}
                imageAllowInversion={false}
                isDarkTheme={isDarkTheme}
                rounded
                visibility={"hidden"}
            />
            <div className="group-scroll-area">
                <div ref={scrollRef} className={`group-scroll${isStreaming ? ' --streaming' : ''}`}>
                    {/* The track is as wide as all the cards (also the ones outside of the visible part), so the one
                        overlay of the streaming grows with them */}
                    <div className="group-scroll__track">
                        {isStreaming && <div className="message-streaming-overlay" aria-hidden="true" />}
                        {messages.map((msg, i) => {
                            const content = msg.content || msg.message || ''
                            return (
                                <div key={msg.messageId || i} className={`card${expandedIndex === i ? ' -expanded' : ''}`}>
                                    {/* No outputFeatures: the copy menu is only for the detail of the card (see SolutionCard), not for the card in the row */}
                                    <MessageWrapper
                                        content={content}
                                        hasContent={!!content}
                                        messageType="assistant"
                                    >
                                        <Box className="message-box message-box-chatbot">
                                            <SolutionCardWrapper
                                                msg={msg}
                                                features={features}
                                                outputFeatures={outputFeatures}
                                                isDarkTheme={isDarkTheme}
                                                onExpandChange={(expanded) => setExpandedIndex(expanded ? i : null)}
                                            />
                                        </Box>
                                    </MessageWrapper>
                                </div>
                            )
                        })}
                    </div>
                </div>
                {showPager && <HorizontalPager page={page} pageCount={pageCount} onPageChange={goToPage} />}
            </div>
        </div>
    )
}

const GroupMessage = React.memo(GroupMessageComponent)

export default GroupMessage
